import { I18n } from "@assets/strings";
import React, { JSX, useState } from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useIsLearner } from "@domain/currentProfile/hooks";
import { useWorkspaceArticles } from "@domain/lms/article/hooks";
import { useWorkspaceCourses } from "@domain/lms/course/hooks";
import { useWorkspaceQuizzes } from "@domain/lms/quiz/hooks";
import { useIsMobile } from "@utils/hooks";
import { ArticleThumbnail } from "@ui/components/article/articleThumbnail";
import { ArticleThumbnailSmall } from "@ui/components/article/articleThumbnailSmall";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { Carousel } from "@ui/components/carousel/carousel";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Secondary } from "@ui/components/containers/textBlocks";
import { Content } from "@ui/components/contentSelector";
import { CourseThumbnail } from "@ui/components/course/courseThumbnail";
import { CourseThumbnailSmall } from "@ui/components/course/courseThumbnailSmall";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { RadioButton } from "@ui/components/inputs/radio";
import { Row, Stack } from "@ui/components/layout";
import { QuizThumbnail } from "@ui/components/quiz/quizThumbnail";
import { theme } from "@ui/styles/theme";
import { gap, mobile } from "@ui/styles/utils";

const CAROUSEL_ITEMS = 10;

interface WorkspaceLearningContentProps extends React.HTMLAttributes<HTMLDivElement> {
	workspaceId: string;
	isAdmin: boolean;
}
interface LearningContentState {
	total: number;
	path: string;
	hasContent: boolean;
}
export const WorkspaceLearningContent: React.FC<WorkspaceLearningContentProps> = ({
	workspaceId,
	isAdmin,
	...htmlProps
}) => {
	const {
		result: courses,
		loading: coursesLoading,
		fetchMore: fetchMoreCourses,
		totalElements: totalCourses,
	} = useWorkspaceCourses(workspaceId);

	const {
		result: quizzes,
		loading: quizzesLoading,
		fetchMore: fetchMoreQuizzes,
		totalElements: totalQuizzes,
	} = useWorkspaceQuizzes(workspaceId);

	const {
		result: articles,
		loading: articleLoading,
		fetchMore: fetchMoreArticles,
		totalElements: totalArticles,
	} = useWorkspaceArticles(workspaceId);

	const [currentContent, setCurrentContent] = useState<Content | undefined>(undefined);
	const hasCourses = totalCourses !== undefined && totalCourses > 0;
	const hasQuizzes = totalQuizzes !== undefined && totalQuizzes > 0;
	const hasArticles = totalArticles !== undefined && totalArticles > 0;
	const isMobile = useIsMobile();
	const isLearner = useIsLearner();
	const canCreate = isAdmin && !isLearner;

	const getContentState = (content: Content): LearningContentState => {
		return content === "courses"
			? {
					total: totalCourses ?? 0,
					path: Path.Lms.Editor("new", workspaceId),
					hasContent: hasCourses,
				}
			: content === "quizzes"
				? {
						total: totalQuizzes ?? 0,
						path: Path.QuizEditor("new", workspaceId),
						hasContent: hasQuizzes,
					}
				: {
						total: totalArticles ?? 0,
						path: Path.ArticleEditor("new", workspaceId),
						hasContent: hasArticles,
					};
	};

	const getContentTitle = (content: Content): JSX.Element => {
		const { total, hasContent, path } = getContentState(content);
		return (
			<Row gap={6} align="center">
				<Subtitle>{I18n.t(`workspace.${content}.title`, { count: total })}</Subtitle>
				{canCreate && hasContent && (
					<Link to={path}>
						<RoundedPlusButton />
					</Link>
				)}
			</Row>
		);
	};

	const getEmptyContent = (content: Content): JSX.Element | undefined => {
		if (getContentState(content).hasContent) {
			return;
		}

		return canCreate ? (
			<Link to={getContentState(content).path}>
				<PrimaryButton>{I18n.t(`workspace.${content}.incentive`)}</PrimaryButton>
			</Link>
		) : (
			<Secondary>{I18n.t(`workspace.${content}.empty`)}</Secondary>
		);
	};

	return (
		<Container {...htmlProps}>
			{!isMobile && (
				<FilterWrapper>
					<PrimaryMedium>{I18n.t("workspace.filter.title")}</PrimaryMedium>
					<Filter>
						<RadioButton
							id={"all-content"}
							name="all-content"
							label={I18n.t("search.filter.type.all")}
							labelStyle={theme.textStyles.light}
							checked={!currentContent}
							onChange={() => setCurrentContent(undefined)}
						/>
						{(["courses", "quizzes", "articles"] as const).map((content) => (
							<RadioButton
								key={content}
								id={content}
								name={content}
								label={I18n.t(`workspace.filter.${content}`)}
								labelStyle={theme.textStyles.light}
								checked={currentContent === content}
								onChange={() => setCurrentContent(content)}
							/>
						))}
					</Filter>
				</FilterWrapper>
			)}
			<ContentWrapper>
				{!currentContent || isMobile ? (
					<Carousels>
						{hasCourses ? (
							<Carousel
								title={getContentTitle("courses")}
								items={isMobile ? courses : courses.slice(0, CAROUSEL_ITEMS)}
								renderItem={(course) => <CourseThumbnailSmall key={course.id} course={course} />}
								loading={coursesLoading}
								onSeeAllAction={
									!isMobile && totalCourses !== undefined && totalCourses > CAROUSEL_ITEMS
										? () => setCurrentContent("courses")
										: undefined
								}
							/>
						) : (
							<Stack gap={16}>
								{getContentTitle("courses")}
								{getEmptyContent("courses")}
							</Stack>
						)}
						{hasQuizzes ? (
							<Carousel
								title={getContentTitle("quizzes")}
								items={isMobile ? quizzes : quizzes.slice(0, CAROUSEL_ITEMS)}
								renderItem={(quiz) => <QuizThumbnail key={quiz.id} quiz={quiz} small />}
								loading={quizzesLoading}
								onSeeAllAction={
									!isMobile && totalQuizzes !== undefined && totalQuizzes > CAROUSEL_ITEMS
										? () => setCurrentContent("quizzes")
										: undefined
								}
							/>
						) : (
							<Stack gap={16}>
								{getContentTitle("quizzes")}
								{getEmptyContent("quizzes")}
							</Stack>
						)}
						{hasArticles ? (
							<Carousel
								title={getContentTitle("articles")}
								items={isMobile ? articles : articles.slice(0, CAROUSEL_ITEMS)}
								renderItem={(article) => <ArticleThumbnailSmall key={article.id} article={article} forInsight />}
								loading={articleLoading}
								onSeeAllAction={
									!isMobile && totalArticles !== undefined && totalArticles > CAROUSEL_ITEMS
										? () => setCurrentContent("articles")
										: undefined
								}
								insightForClassroom
							/>
						) : (
							<Stack gap={16}>
								{getContentTitle("articles")}
								{getEmptyContent("articles")}
							</Stack>
						)}
					</Carousels>
				) : (
					<Stack gap={16} justify="flex-start" align="stretch">
						{getContentTitle(currentContent)}
						{getEmptyContent(currentContent)}
						<SpecificContent>
							{currentContent === "courses" ? (
								<InfiniteScroll onEndReached={fetchMoreCourses} loading={coursesLoading}>
									{courses.map((course) => (
										<CourseThumbnail
											course={course}
											key={course.id}
											displayStatus={isAdmin && course.provider === "TOOSHARE"}
										/>
									))}
								</InfiniteScroll>
							) : currentContent === "quizzes" ? (
								<InfiniteScroll onEndReached={fetchMoreQuizzes} loading={quizzesLoading}>
									{quizzes.map((quiz) => (
										<QuizThumbnail quiz={quiz} key={quiz.id} displayStatus={isAdmin} />
									))}
								</InfiniteScroll>
							) : (
								<InfiniteScroll onEndReached={fetchMoreArticles} loading={articleLoading}>
									{articles.map((article) => (
										<ArticleThumbnail article={article} key={article.id} displayStatus={isAdmin} />
									))}
								</InfiniteScroll>
							)}
						</SpecificContent>
					</Stack>
				)}
			</ContentWrapper>
		</Container>
	);
};

const Container = styled.div`
	display: grid;
	grid-template-columns: 280px minmax(0, 1fr);
	grid-template-rows: auto;
	grid-column-gap: 20px;
	grid-row-gap: 24px;
	grid-template-areas: "filters main";

	@media ${mobile} {
		grid-template-columns: 100%;
		grid-template-areas: "main";
	}
`;

const ContentWrapper = styled.div`
	grid-area: main;
`;

const Carousels = styled.div`
	${gap(40)};
`;

const Subtitle = styled.div`
	${({ theme }) => theme.textStyles.subtitle};
`;

const FilterWrapper = styled.div`
	margin-top: 14px;
	${gap(13)};
	grid-area: filters;
`;

const Filter = styled(RoundedBlock)`
	width: 100%;
	padding: 20px;
	${gap(7)};
`;

const SpecificContent = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	grid-gap: 20px;
`;
